import {
  SNOW_OBSERVATION_SOURCE,
  STATIC_MARKUPS_SOURCE,
  WAYPOINTS_SOURCE,
  HIGHLIGHTED_WAYPOINTS_SOURCE,
  LINES_SOURCE,
  TRACKS_SOURCE,
  SHAPES_SOURCE,
  ROUTES_SOURCE,
  SEARCH_PIN_SOURCE,
  MARKUP_EDIT_SOURCE,
  POINT_OF_INTEREST_SOURCE,
} from '@/lib/enums'
import { MARKUP_LAYER_TYPES } from '@/services/markup-service/constants'
import type MapFeature from '@/lib/map-feature'

/**
 * Is a feature a polygon
 * @param feature - feature from map tiles
 */
export function isPolygonFeature(feature: MapFeature) {
  return ['Polygon', 'MultiPolygon'].includes(feature?.layerType)
}

/**
 * Is a feature a private parcel?
 * @param feature - feature from map tiles
 */
export function isPrivateFeature(feature: MapFeature) {
  return feature.layer?.includes('private') ?? false
}

/**
 * Is a feature a public parcel?
 * @param feature - feature from map tiles
 */
export function isPublicFeature(feature: MapFeature) {
  return feature.layer?.includes('public') ?? false
}

/**
 * Is a feature a rich content feature? Could be lines, routes, labels, etc
 * @param feature - feature from map tiles
 */
export function isRichContentFeature(feature: MapFeature) {
  return feature?.layer?.includes('rich_content') ?? false
}

/**
 * Is a feature a markup feature?
 * @param feature - feature from map tiles
 */
export function isMarkupFeature(feature: MapFeature) {
  return (
    feature?.source === STATIC_MARKUPS_SOURCE ||
    feature?.source === WAYPOINTS_SOURCE ||
    feature?.source === HIGHLIGHTED_WAYPOINTS_SOURCE ||
    feature?.source === LINES_SOURCE ||
    feature?.source === TRACKS_SOURCE ||
    feature?.source === SHAPES_SOURCE ||
    feature?.source === ROUTES_SOURCE ||
    feature?.source === MARKUP_EDIT_SOURCE
  )
}

/**
 * Is a feature a markup LineString feature?
 * @param feature - feature from map tiles
 */
export function isMarkupLineStringFeature(feature: MapFeature) {
  return (
    isMarkupFeature(feature) &&
    (feature?.geometry?.type === MARKUP_LAYER_TYPES.LINE ||
      feature?.geometry?.type === 'MultiLineString')
  )
}

/**
 * Is a feature a markup line feature?
 * @param feature - feature from map tiles
 */
export function isMarkupLineFeature(feature: MapFeature) {
  return (
    isMarkupLineStringFeature(feature) && feature?.properties?.type === 'line'
  )
}

/**
 * Is a feature a markup track feature?
 * @param feature - feature from map tiles
 */
export function isMarkupTrackFeature(feature: MapFeature) {
  return (
    isMarkupLineStringFeature(feature) && feature?.properties?.type === 'track'
  )
}

/**
 * Is a feature a markup area feature?
 * @param feature - feature from map tiles
 */
export function isMarkupAreaFeature(feature: MapFeature) {
  return (
    isMarkupFeature(feature) &&
    feature?.geometry?.type === MARKUP_LAYER_TYPES.AREA
  )
}

/**
 * Is a feature a markup waypoint feature?
 * @param feature - feature from map tiles
 */
export function isMarkupWaypointFeature(feature: MapFeature) {
  return (
    isMarkupFeature(feature) &&
    feature?.geometry?.type === MARKUP_LAYER_TYPES.WAYPOINT
  )
}

/**
 * Is a feature a markup route feature?
 * @param  feature - feature from map
 */
export function isMarkupRouteFeature(feature: MapFeature) {
  return isMarkupFeature(feature) && feature.properties?.type === 'route'
}

/**
 * Is a feature a search result marker feature?
 * @param  feature - feature from map
 */
export function isSearchResultFeature(feature: MapFeature) {
  return feature?.source === SEARCH_PIN_SOURCE
}

/**
 * Existing city types returned from search service with corresponding zoom levels
 * If a city search result is selected, set map zoom to appropriate level
 */
export const cityTypeMap = [
  'city_continental',
  'city_major',
  'city_xlarge',
  'city_large',
  'city_small',
  'town',
  'city',
]

/**
 * Determines if a search result is a city type
 * @param type - A search feature type from the API
 */
export const isCity = (type: string) =>
  cityTypeMap.includes(type?.toLowerCase())

/**
 * Determines if a search result is a county type
 * @param type - A search feature type from the API
 */
export const isCounty = (type: string) => type?.toLowerCase() === 'county'

/**
 * Determine if search feature is a city or county by onxType
 * @param  type - A search feature type from the API
 */
export const isCityOrCounty = (type: string) => {
  return isCity(type) || isCounty(type)
}

/**
 * Is a feature a snotel pin?
 * @param feature - feature from map tiles
 */
export function isSnotel(feature: MapFeature) {
  return (
    (feature?.layer?.includes('weather') &&
      feature?.properties['data:station_type'] === 'SNOTEL') ??
    false
  )
}

/**
 * Is a feature a snow observation feature
 * @param feature - feature from map tiles
 */
export function isSnowObservationFeature(feature: MapFeature) {
  return feature.sourceLayer === SNOW_OBSERVATION_SOURCE
}

/**
 * Get the identifiers for a feature
 * @param feature - feature from map tiles
 */
export function getFeatureIdentifiers(feature: MapFeature): string[] {
  const raw = feature.properties?.['data:onx:identifiers']
  if (Array.isArray(raw)) {
    return raw
  }

  if (typeof raw === 'string') {
    return raw.split(';')
  }

  return []
}

/**
 * Is a feature a point of interest feature
 * @param feature - feature from map tiles
 */
export function isPointOfInterestFeature(feature: MapFeature) {
  return feature.sourceLayer === POINT_OF_INTEREST_SOURCE
}
